import { createSelector } from "@reduxjs/toolkit";
import { SharedState } from "../../SharedProvider";
import {
  selectCleaningZones,
  selectRepeatPaths,
  selectGatesData,
  selectLandmarksData,
  selectRedZones,
  selectSelectedRoomCleaningZones,
  selectSelectedRoomRedZones,
  selectWaypointsData,
  selectRuleZones,
  selectSelectedRoomRuleZones,
} from "../room-selectors";
import CleaningZone from "../../../interfaces/robot/rooms/cleaning-zone";
import RedZone from "../../../interfaces/robot/rooms/red-zone";
import RuleZone from "../../../interfaces/robot/rooms/rule-zone";
import { getPolygonAreaString } from "../../../utils/map/zone-helper";
import Gate from "../../../interfaces/robot/rooms/gate";
import Landmark from "../../../interfaces/robot/rooms/landmark";
import TeachAndRepeatZonePath from "../../../interfaces/robot/rooms/teach-and-repeat-zone-path";
import { WaypointEdge, WaypointNode } from "../../../interfaces/robot/rooms/waypoints";
import { Shared } from "react-redux";

export const selectZonePlannerModeState = (state: SharedState) => state.rooms.zonePlanner.plannerMode;
export const selectZonePlannerMouseState = (state: SharedState) => state.rooms.zonePlanner.mouseMode;
export const selectCurrentTeachingAutoZonePath = (state: SharedState) =>
  state.rooms.zonePlanner.currentTeachingAutoZonePath;
export const selectZoneIdsSentToBackOrder = (state: SharedState) => state.rooms.zonePlanner.zoneIdsSentToBackOrder;
export const selectHiddenZoneIds = (state: SharedState) => state.rooms.zonePlanner.hiddenZoneIds;
export const selectHiddenGateIds = (state: SharedState) => state.rooms.zonePlanner.hiddenGateIds;
export const selectHiddenLandmarkIds = (state: SharedState) => state.rooms.zonePlanner.hiddenLandmarkIds;

export const selectCleaningZonesRestSet = (state: SharedState) => state.rooms.cleaningZones.rest;
export const selectRedZonesRestSet = (state: SharedState) => state.rooms.redZones.rest;
export const selectRuleZonesRestSet = (state: SharedState) => state.rooms.ruleZones.rest;
export const selectGatesRestSet = (state: SharedState) => state.rooms.gates.rest;
export const selectLandmarksRestSet = (state: SharedState) => state.rooms.landmarks.rest;
export const selectLimitZonesRestSet = (state: SharedState) => state.rooms.limitZones.rest;
export const selectWaypointsRestSet = (state: SharedState) => state.rooms.waypoints.rest;

export const selectZoneById = createSelector(
  [selectCleaningZones, selectRedZones, selectRuleZones, (_state: SharedState, zoneId: string | null) => zoneId],
  (cleaningZones, redZones, ruleZones, zoneId) => {
    if (zoneId == null) {
      return null;
    }

    let zoneInstance: CleaningZone | RedZone | RuleZone | null = null;
    zoneInstance = cleaningZones.find((cz) => cz.id === zoneId) ?? null;
    zoneInstance = zoneInstance ?? redZones.find((rz) => rz.id === zoneId) ?? null;
    zoneInstance = zoneInstance ?? ruleZones.find((rz) => rz.id === zoneId) ?? null;

    return zoneInstance;
  },
);

const selectZoneIdsOrderedByArea = createSelector([selectCleaningZones, selectRedZones, selectRuleZones], (cleaningZones, redZones, ruleZones) => {
  const idsAndAreas: { id: string; area: number }[] = [];
  cleaningZones.forEach((z) =>
    idsAndAreas.push({
      id: z.id,
      area: parseFloat(
        getPolygonAreaString(
          z.points.map((p) => p.point),
          1,
        ),
      ),
    }),
  );
  redZones.forEach((z) =>
    idsAndAreas.push({
      id: z.id,
      area: parseFloat(
        getPolygonAreaString(
          z.points.map((p) => p.point),
          1,
        ),
      ),
    }),
  );
  ruleZones.forEach((z) =>
    idsAndAreas.push({
      id: z.id,
      area: parseFloat(
        getPolygonAreaString(
          z.points.map((p) => p.point),
          1,
        ),
      ),
    }),
  );
  return idsAndAreas.sort((a, b) => b.area - a.area).map((z) => z.id);
});

export const selectAllZoneIds = createSelector(
  [selectZoneIdsOrderedByArea, selectZoneIdsSentToBackOrder],
  (zoneIdsByArea, zoneIdsBackOrder) => {
    // adds zones sent to back list first so they're rendered at the back
    return zoneIdsBackOrder.concat(zoneIdsByArea.filter((id) => !zoneIdsBackOrder.includes(id)));
  },
);
export const selectSelectedZone = (state: SharedState) => state.rooms.zonePlanner.selectedZone;

export const selectChargingStationDragData = (state: SharedState) => state.rooms.zonePlanner.chargingStationDrag;

export const selectSelectedZoneInstance = createSelector(
  [selectSelectedZone, selectSelectedRoomCleaningZones, selectSelectedRoomRedZones, selectSelectedRoomRuleZones],
  (selectedZone, cleaningZones, redZones, ruleZones): CleaningZone | RedZone | RuleZone | null => {
    let selectedZoneInstance: CleaningZone | RedZone | RuleZone | null = null;
    if (selectedZone.id != null) {
      selectedZoneInstance = cleaningZones.find((cz) => cz.id === selectedZone.id) ?? null;
      selectedZoneInstance = selectedZoneInstance ?? redZones.find((rz) => rz.id === selectedZone.id) ?? null;
      selectedZoneInstance = selectedZoneInstance ?? ruleZones.find((rz) => rz.id === selectedZone.id) ?? null;
    }

    return selectedZoneInstance;
  },
);
export const selectSelectedGate = createSelector([selectGatesData], (gatesData): Gate | null => {
  return gatesData.data.find((gate) => gate.id === gatesData.selectedId) ?? null;
});
export const selectSelectedLandmark = createSelector([selectLandmarksData], (landmarksData): Landmark | null => {
  return landmarksData.data.find((gate) => gate.id === landmarksData.selectedId) ?? null;
});
export const selectSelectedWaypointNode = createSelector(
  [selectWaypointsData],
  (waypointsData): WaypointNode | null => {
    return waypointsData.data.nodes.find((node) => node.id === waypointsData.selectedNodeId) ?? null;
  },
);
export const selectSelectedWaypointEdge = createSelector(
  [selectWaypointsData],
  (waypointsData): WaypointEdge | null => {
    return waypointsData.data.edges.find((node) => node.id === waypointsData.selectedEdgeId) ?? null;
  },
);
export const selectGeneratingTeachPathMessage = (state: SharedState) =>
  state.rooms.zonePlanner.generatingTeachPathMessage;
export const selectIsGeneratingTeachPath = (state: SharedState) => state.rooms.zonePlanner.isGeneratingTeachPath;

export const selectTeachAndRepeatPath = createSelector(
  [selectRepeatPaths, selectSelectedZone],
  (repeatPaths, selectedZone): TeachAndRepeatZonePath | null => {
    return repeatPaths.find((rp_path) => rp_path.zoneId === selectedZone.id) ?? null;
  },
);

export const selectTeachAndRepeatPathByZoneId = createSelector(
  [selectRepeatPaths, (_state: SharedState, zoneId: string | null) => zoneId],
  (repeatPaths, zoneId) => {
    const path = repeatPaths.find((rp_path) => rp_path.zoneId === zoneId);
    if (path === undefined) return null;
    return path as TeachAndRepeatZonePath;
  },
);

export const selectMapHistory = (state: SharedState) => state.rooms.zonePlanner.history;